import { useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Cards in a CSS grid have no coordinates to animate — reorder them and they
 * are simply somewhere else on the next paint. So: measure where each one
 * was, let the browser put it where it now belongs, then transform it back to
 * where it was and release it. It slides.
 *
 * The board doesn't need this. Its tiles are positioned by the masonry, so
 * changing the order already changes a transform, which already animates.
 *
 * `--drop-x`/`--drop-y` rather than `transform` directly, because the element
 * may already have a transform of its own from the layout it lives in. This
 * composes with it instead of overwriting it.
 */

const EASE = 'transform 260ms cubic-bezier(0.2, 0, 0, 1)';
const DONE_MS = 300;

export function useFlip(
  container: RefObject<HTMLElement | null>,
  /** Changes when the order does. */
  key: string,
  /** The lifted card, whose position belongs to the drag. */
  skip: string | undefined,
): void {
  const previous = useRef(new Map<string, { rect: DOMRect; skipped: boolean }>());

  useLayoutEffect(() => {
    const root = container.current;
    if (root === null) return;

    const before = previous.current;
    const now = new Map<string, { rect: DOMRect; skipped: boolean }>();

    for (const node of root.querySelectorAll<HTMLElement>('[data-sort-id]')) {
      const id = node.dataset.sortId;
      if (id === undefined) continue;

      const rect = node.getBoundingClientRect();
      const skipped = id === skip;
      now.set(id, { rect, skipped });

      const was = before.get(id);
      // A card that was lifted last time was measured mid-air, over the
      // cursor. Sliding it in from there on release would be an animation of
      // the drag replaying itself.
      if (was === undefined || skipped || was.skipped) continue;

      slide(node, was.rect, rect);
    }

    previous.current = now;
  }, [container, key, skip]);
}

function slide(node: HTMLElement, from: DOMRect, to: DOMRect): void {
  const dx = from.left - to.left;
  const dy = from.top - to.top;
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

  node.style.transition = 'none';
  node.style.setProperty('--drop-x', `${dx}px`);
  node.style.setProperty('--drop-y', `${dy}px`);

  requestAnimationFrame(() => {
    node.style.transition = EASE;
    node.style.removeProperty('--drop-x');
    node.style.removeProperty('--drop-y');
    window.setTimeout(() => node.style.removeProperty('transition'), DONE_MS);
  });
}
